import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Link } from 'react-router-dom';
import { Swords, ArrowRight } from 'lucide-react';
import { usePlayerPBs } from './api';
import { useAuth } from '@/providers/AuthProvider';
import { useCurrentProfile } from '@/features/auth/api';
import { getMiniGame } from '@/features/games/_framework/registry';
import { Card, CardDescription, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Loading';
import { Avatar } from '@/components/ui/Avatar';
import { cn, formatCompact } from '@/lib/utils';
/**
 * Carte de comparaison directe : pour chaque mini-jeu où l'un OU l'autre a
 * un PB, on affiche les deux scores côte à côte avec l'indicateur "qui mène".
 */
export function ComparisonCard({ otherId, otherPseudo, otherAvatar, }) {
    const { user } = useAuth();
    const { data: meProfile } = useCurrentProfile();
    const { data: minePbs, isLoading: mineLoading } = usePlayerPBs(user?.id);
    const { data: theirPbs, isLoading: theirLoading } = usePlayerPBs(otherId);
    if (!user || !meProfile) {
        return null;
    }
    if (mineLoading || theirLoading) {
        return _jsx(Skeleton, { className: "h-32 w-full" });
    }
    const map = new Map();
    for (const pb of minePbs ?? []) {
        const k = `${pb.game_type}|${pb.mode}`;
        map.set(k, { game_type: pb.game_type, mode: pb.mode, mine: pb.best_score, theirs: null });
    }
    for (const pb of theirPbs ?? []) {
        const k = `${pb.game_type}|${pb.mode}`;
        const cur = map.get(k);
        if (cur)
            cur.theirs = pb.best_score;
        else
            map.set(k, { game_type: pb.game_type, mode: pb.mode, mine: null, theirs: pb.best_score });
    }
    const rows = [...map.values()].sort((a, b) => a.game_type.localeCompare(b.game_type));
    if (rows.length === 0) {
        return (_jsx(Card, { padding: "md", children: _jsxs("div", { className: "flex items-start gap-3", children: [_jsx(Swords, { className: "h-5 w-5 text-ink-500" }), _jsxs("div", { children: [_jsx(CardTitle, { className: "text-base", children: "Comparaison directe" }), _jsx(CardDescription, { className: "mt-1", children: "Aucun de vous deux n'a encore de score class\u00E9. Lance une partie !" })] })] }) }));
    }
    // Wins counts
    let myWins = 0;
    let theirWins = 0;
    for (const r of rows) {
        const s = winner(r.mine, r.theirs);
        if (s === 'me')
            myWins++;
        else if (s === 'other')
            theirWins++;
    }
    return (_jsxs(Card, { variant: "glow", padding: "md", children: [_jsxs("div", { className: "mb-4 flex items-center gap-2", children: [_jsx(Swords, { className: "h-5 w-5 text-flame-400" }), _jsxs(CardTitle, { className: "text-base", children: ["Toi vs ", otherPseudo] })] }), _jsxs("div", { className: "mb-4 grid grid-cols-3 items-center gap-2 text-center", children: [_jsxs("div", { children: [_jsx(Avatar, { pseudo: meProfile.pseudo, src: meProfile.avatar_url, size: "lg", border: myWins > theirWins ? 'gold' : 'electric' }), _jsx("p", { className: "mt-1 text-xs font-semibold text-ink-200", children: "Toi" }), _jsx("p", { className: "font-display text-2xl text-ink-50", children: myWins })] }), _jsxs("div", { className: "text-ink-500", children: [_jsx("p", { className: "font-display text-xs uppercase tracking-wider", children: "VS" }), _jsxs("p", { className: "text-2xs mt-2 text-ink-400", children: [rows.length, " jeu", rows.length > 1 ? 'x' : ''] })] }), _jsxs("div", { children: [_jsx(Avatar, { pseudo: otherPseudo, src: otherAvatar, size: "lg", border: theirWins > myWins ? 'gold' : 'electric' }), _jsx("p", { className: "mt-1 truncate text-xs font-semibold text-ink-200", children: otherPseudo }), _jsx("p", { className: "font-display text-2xl text-ink-50", children: theirWins })] })] }), _jsx("ul", { className: "space-y-2", children: rows.map((r) => (_jsx(CompRow, { row: r }, `${r.game_type}-${r.mode}`))) })] }));
}
function CompRow({ row, }) {
    const def = getMiniGame(row.game_type);
    const w = winner(row.mine, row.theirs);
    return (_jsx("li", { children: _jsxs(Link, { to: `/leaderboard?game=${encodeURIComponent(row.game_type)}`, className: "flex items-center gap-3 rounded-2xl bg-ink-900/40 p-3 transition-colors hover:bg-ink-900/70", children: [_jsx("span", { "aria-hidden": true, className: "text-xl", children: def?.emoji ?? '🎮' }), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("p", { className: "font-display truncate text-sm font-semibold text-ink-50", children: def?.name ?? row.game_type }), _jsx(Badge, { size: "sm", variant: row.mode === 'daily' ? 'flame' : 'electric', children: row.mode })] }), _jsx(Score, { value: row.mine, highlight: w === 'me' ? 'win' : w === 'other' ? 'loss' : 'tie' }), _jsx(ArrowRight, { className: "h-3.5 w-3.5 text-ink-600" }), _jsx(Score, { value: row.theirs, highlight: w === 'other' ? 'win' : w === 'me' ? 'loss' : 'tie' })] }) }));
}
function Score({ value, highlight, }) {
    if (value === null) {
        return _jsx("span", { className: "w-16 text-right font-mono text-xs text-ink-500", children: "\u2014" });
    }
    return (_jsx("span", { className: cn('w-16 text-right font-display text-base tabular-nums', highlight === 'win' && 'text-emerald-300', highlight === 'loss' && 'text-rose-300', highlight === 'tie' && 'text-ink-200'), children: formatCompact(value) }));
}
function winner(mine, theirs) {
    if (mine === null && theirs === null)
        return 'unknown';
    if (mine === null)
        return 'other';
    if (theirs === null)
        return 'me';
    if (mine > theirs)
        return 'me';
    if (theirs > mine)
        return 'other';
    return 'tie';
}
